import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Dialog, Field, FIELD_CLASS } from '@/components/Dialog'
import { createClient } from '@/features/clients/api'
import type { ApiError } from '@/lib/api/client'
import type { Client } from '@/types/support'

/**
 * Adding a client.
 *
 * Only the name is required, because the moment you need a client record is
 * usually the moment you are starting a project for them and do not yet know
 * who the contact will be. Sector, contact and health all get filled in as the
 * relationship becomes real.
 */
export function NewClientDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [name, setName] = useState('')
  const [sector, setSector] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')

  const create = useMutation<Client, ApiError, void>({
    mutationFn: () =>
      createClient({
        name: name.trim(),
        sector: sector.trim() === '' ? null : sector.trim(),
        primaryContactName: contactName.trim() === '' ? null : contactName.trim(),
        primaryContactEmail: contactEmail.trim() === '' ? null : contactEmail.trim(),
      }),
    onSuccess: () => {
      // The new-project form reads the same list, so it is current the next
      // time it opens.
      void queryClient.invalidateQueries({ queryKey: ['clients'] })
      void queryClient.invalidateQueries({ queryKey: ['portfolio'] })
      onClose()
    },
  })

  const ready = name.trim().length > 0

  return (
    <Dialog title={t('clients.newClient')} onClose={onClose}>
      <form
        className="flex flex-col gap-3.5 px-5 py-4"
        onSubmit={(event) => {
          event.preventDefault()
          if (ready) create.mutate()
        }}
      >
        <Field label={t('clients.name')}>
          <input
            value={name}
            autoFocus
            disabled={create.isPending}
            onChange={(event) => setName(event.target.value)}
            className={FIELD_CLASS}
          />
        </Field>

        <Field label={t('clients.sector')}>
          <input
            value={sector}
            disabled={create.isPending}
            onChange={(event) => setSector(event.target.value)}
            className={FIELD_CLASS}
          />
        </Field>

        <Field label={t('clients.contactName')}>
          <input
            value={contactName}
            disabled={create.isPending}
            onChange={(event) => setContactName(event.target.value)}
            className={FIELD_CLASS}
          />
        </Field>

        <Field label={t('clients.contactEmail')}>
          <input
            value={contactEmail}
            type="email"
            disabled={create.isPending}
            onChange={(event) => setContactEmail(event.target.value)}
            className={FIELD_CLASS}
          />
        </Field>

        {create.isError ? (
          <p className="text-[12px] text-[color:var(--color-state-blocked)]">
            {create.error.message}
          </p>
        ) : null}

        <div className="flex items-center gap-3">
          <p className="text-ink-500 min-w-0 flex-1 text-[11px] leading-tight">
            {t('clients.optional')}
          </p>
          <button
            type="submit"
            disabled={!ready || create.isPending}
            className="bg-brand-600 shrink-0 rounded px-3.5 py-1.5 text-[13px] font-medium text-white disabled:opacity-40"
          >
            {create.isPending ? t('clients.creating') : t('clients.create')}
          </button>
        </div>
      </form>
    </Dialog>
  )
}
